import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { getConfiguration } from '../../api/configuration';
import type {
  Logo,
  StoreConfiguration,
  StoreCustomerConfig,
  StoreShopConfig,
} from '../../types/storeConfig';
import type { WindowWika } from '../../types/window';

const storeId = 'store-configuration';

// TODO: store smaller config chunks

export const useStoreConfiguration = defineStore(storeId, () => {
  const config = ref<StoreConfiguration>();
  const configCustomer = ref<undefined | StoreCustomerConfig>();
  const configShop = ref<undefined | StoreShopConfig>();
  const configLogo = ref<undefined | Logo>();

  const { _CAAS_config: caasConfig } = window as unknown as WindowWika;
  const caasUrl = caasConfig?.baseUrl;

  async function init() {
    if (!caasConfig && !caasUrl) {
      console.error(`${storeId} >>> error`, 'caas configuration or URL is not available');
    } else {
      const {
        configServiceUrl,
        instance,
        language,
        locale,
        masterLocale,
        masterProject,
        project,
        stage,
        type,
        prefix,
      } = caasConfig ?? {};

      config.value = await getConfiguration(
        caasUrl,
        configServiceUrl,
        project,
        instance,
        stage,
        language,
        locale,
        masterLocale,
        masterProject,
        type,
        prefix,
      );

      return;
    }
  }

  watch(config, () => {
    configCustomer.value = config.value && {
      shopBaseUrl: config.value.pt_shop_system_url,
      loginUrl: config.value.caas_loginUrl,
      logoutUrl: config.value.caas_logoutUrl,
      forgotPasswordAction: config.value.caas_shopPasswordAction,
      checkLoginUrl: config.value.caas_checkLoginUrl,
      registrationAction: config.value.caas_shopRegistrationAction,
      iFramePage: config.value.pt_iframe_page.route,
      accountAction: config.value.caas_shopOverviewAction,
      accountEditAction: config.value.caas_shopSelfserviceAction,
      addressAction: config.value.caas_shopAddressAction,
      historyAction: config.value.caas_shopHistoryAction,
      startPage: config.value.pt_shop_start_page,
      isSSOavailable: config.value.pt_use_sso_login,
      ssoUrl: config.value.pt_sso_url,
    };

    configShop.value = config.value && {
      getCartUrl: config.value.caas_getCartUrl,
      removeItemUrl: config.value.caas_removeItemUrl,
      updateQuantityURL: config.value.caas_updateItemQuantityUrl,
      showCartAction: config.value.caas_shopCartAction,
      shippingInfoUrl: config.value.ps_shipping_information_page,
      formatCurrency: config.value.pt_format_currency,
      currencyCode: config.value.pt_format_currencyCode,
      currencyPosition: config.value.pt_format_currency_position,
      precision: config.value.pt_format_precision,
      decimalSeparator: config.value.pt_format_decimal_separator,
      thousandSeparator: config.value.pt_format_thousands_separator,
      deliveryCategoryThreshold: Number(config.value.pt_delivery_category_threshold ?? 0),
      checkoutUrl: config.value.caas_checkLoginUrl,
      getQuoteIdUrl: config.value.caas_getQuoteIdUrl,
      getItemInfoUrl: config.value.caas_getItemInfoUrl,
      addItemUrl: config.value.caas_addItemUrl,
      container: config.value.pt_search_container,
      shopContainer: config.value.pt_search_shop_container,
      // TODO selectOptionMax FE constants
    };

    configLogo.value =
      config.value &&
      ({
        startpageUrl:
          caasConfig?.instance === 'shop'
            ? config.value.pt_shop_start_page
            : config.value.pt_local_startpage.route,
        mediaOriginal: {
          mediaUrl:
            config.value.pt_logo_image_src.url ||
            config.value.pt_logo_image_src.resolutionsMetaData?.ORIGINAL.url,
        },
        mediaInverted: {
          mediaUrl:
            config.value.pt_logo_image_white_src.url ||
            config.value.pt_logo_image_white_src.resolutionsMetaData?.ORIGINAL.url,
        },
      } satisfies Logo);
  });

  return { init, config, caasUrl, caasConfig, configCustomer, configShop, configLogo };
});
